/**
 * Main-thread music backend: commands go to the AudioWorklet PatternPlayer.
 * Notes are scheduled on the audio thread against real currentTime.
 */
import type { ClipIR } from "../clip/types";
import type { PlayheadInfo } from "../clip/patternPlayer";
import { MAX_INSTRUMENT_SLOT_COUNT, type InstrumentSlot, type KernelMessage } from "../types";
import type { AudioEngine } from "./engine";
import type { WorkletMusicIn, WorkletMusicOut } from "./workletMusicProtocol";
import { isWorkletMusicOut } from "./workletMusicProtocol";

export type WorkletMusicProxyOptions = {
  engine: AudioEngine;
  lookahead?: number;
  onPlayhead?: (info: PlayheadInfo | undefined) => void;
  onEnded?: () => void;
  onKernelMessage?: (message: KernelMessage) => void;
};

/**
 * Music control surface matching PatternPlayer / SchedulerProxy for AudioSession.
 */
export class WorkletMusicProxy {
  private playing = false;
  private readonly muted: boolean[] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => false);
  private readonly solo: boolean[] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => false);
  private openHoldFlags: boolean[] = [];
  private playheadRaf: number | undefined;
  private pendingPlayhead: PlayheadInfo | undefined;
  private hasPendingPlayhead = false;
  private readonly onPort: (data: unknown) => void;

  constructor(private readonly options: WorkletMusicProxyOptions) {
    this.onPort = data => this.onWorkletMessage(data);
    this.options.engine.addPortListener?.(this.onPort);
    this.send({
      type: "music",
      action: "config",
      lookahead: options.lookahead ?? 0.25,
    });
  }

  isPlaying(): boolean {
    return this.playing;
  }

  getMuted(): boolean[] {
    return [...this.muted];
  }

  getSolo(): boolean[] {
    return [...this.solo];
  }

  setClip(clip: ClipIR): void {
    this.send({ type: "music", action: "setClip", clip: structuredClone(clip) });
  }

  start(clip: ClipIR, options?: { startStep?: number }): void {
    this.playing = true;
    this.send({
      type: "music",
      action: "play",
      clip: structuredClone(clip),
      startStep: options?.startStep,
    });
  }

  stop(): void {
    this.playing = false;
    // Silence first on the worklet kernel (same port as music stop).
    this.options.engine.transport.cancelPending(this.options.engine.getCurrentTime());
    this.options.engine.transport.allNotesOff();
    this.send({ type: "music", action: "stop" });
    this.options.onPlayhead?.(undefined);
  }

  setTrackMute(slot: InstrumentSlot, muted: boolean): void {
    this.muted[slot] = muted;
    this.sendMuteSolo();
  }

  setTrackSolo(slot: InstrumentSlot, solo: boolean): void {
    this.solo[slot] = solo;
    this.sendMuteSolo();
  }

  setMuteSolo(state: { muted?: boolean[]; solo?: boolean[] }): void {
    if (state.muted) {
      for (let i = 0; i < MAX_INSTRUMENT_SLOT_COUNT; i++) {
        this.muted[i] = Boolean(state.muted[i]);
      }
    }
    if (state.solo) {
      for (let i = 0; i < MAX_INSTRUMENT_SLOT_COUNT; i++) {
        this.solo[i] = Boolean(state.solo[i]);
      }
    }
    this.sendMuteSolo();
  }

  setOpenHoldDefaultSlots(flags: boolean[]): void {
    this.openHoldFlags = flags.slice(0, MAX_INSTRUMENT_SLOT_COUNT);
    this.send({
      type: "music",
      action: "setOpenHoldDefaultSlots",
      flags: this.openHoldFlags.slice(),
    });
  }

  dispose(): void {
    this.playing = false;
    if (this.playheadRaf !== undefined) {
      cancelAnimationFrame(this.playheadRaf);
      this.playheadRaf = undefined;
    }
    this.options.engine.removePortListener?.(this.onPort);
    try {
      this.send({ type: "music", action: "stop" });
    } catch {
      // engine may already be disposed
    }
  }

  private sendMuteSolo(): void {
    this.send({
      type: "music",
      action: "setMuteSolo",
      muted: this.getMuted(),
      solo: this.getSolo(),
    });
  }

  private send(msg: WorkletMusicIn): void {
    this.options.engine.post(msg as unknown as KernelMessage);
  }

  private onWorkletMessage(data: unknown): void {
    if (!isWorkletMusicOut(data)) {
      return;
    }
    const msg = data as WorkletMusicOut;
    switch (msg.type) {
      case "musicPlayhead": {
        this.pendingPlayhead = msg.info;
        this.hasPendingPlayhead = true;
        if (this.playheadRaf !== undefined) {
          break;
        }
        this.playheadRaf = requestAnimationFrame(() => {
          this.playheadRaf = undefined;
          if (!this.hasPendingPlayhead) {
            return;
          }
          this.hasPendingPlayhead = false;
          this.options.onPlayhead?.(this.pendingPlayhead);
        });
        break;
      }
      case "musicEnded": {
        this.playing = false;
        this.options.onEnded?.();
        break;
      }
      case "musicNote": {
        this.options.onKernelMessage?.(msg.message);
        break;
      }
      default:
        break;
    }
  }
}
